import { formatDate, NgClass } from "@angular/common";
import { AfterViewInit, ChangeDetectorRef, Component, DoCheck, ElementRef, EventEmitter, inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ControlContainer, FormsModule, NgModelGroup, ReactiveFormsModule } from "@angular/forms";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { KeyHandlerService, SecurityService, ToastService } from "@tylertech/core-module";
import { FieldDensity } from "@tylertech/forge";
import { ForgeCircularProgressModule, ForgeDatePickerModule, ForgeDatePickerProxyModule, ForgeTextFieldModule } from "@tylertech/forge-angular";
import { EMPTY_GUID, GuidHelper, Utilities } from "@tylertech/shared-module";
import { CompareType, PropertyMetadata } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { debounceTime, filter, takeUntil } from "rxjs/operators";
import { FocusOnLoadDirective } from "../../../../directives/focusOnLoad.directive";
import { AugmentedModelObject, ModelChangeArg, ValueChangedParms } from "../../../../interfaces";
import { MetadataEventService, MetadataPropertyService, MetadataService } from "../../../../services";
import { ObjectHelpers } from "../../../../utilities";

const VALID_DATE_PATTERN = /\d{1,2}\/?\d{1,2}\/?\d{4}/;
const MONTH_DAY_PATTERN = /^\d{2}\/\d{2}\/$/;
const BEGINNING_OF_WEEK = "bow";
const END_OF_WEEK = "eow";
const BEGINNING_OF_MONTH = "bom";
const END_OF_MONTH = "eom";
const BEGINNING_OF_LAST_MONTH = "bolm";
const END_OF_LAST_MONTH = "eolm";
const BEGINNING_OF_QUARTER = "boq";
const END_OF_QUARTER = "eoq";
const BEGINNING_OF_YEAR = "boy";
const END_OF_YEAR = "eoy";
const BEGINNING_OF_LAST_QUARTER = "bolq";
const END_OF_LAST_QUARTER = "eolq";
const BEGINNING_OF_LAST_YEAR = "boly";
const END_OF_LAST_YEAR = "eoly";

@Component({
    selector: "metadata-input-date",
    templateUrl: "./metadata-input-date.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    imports: [
        ClassDirective,
        FocusOnLoadDirective,
        ForgeCircularProgressModule,
        ForgeDatePickerModule,
        ForgeDatePickerProxyModule,
        ForgeTextFieldModule,
        FormsModule,
        NgClass,
        ReactiveFormsModule,
    ]
})
export class MetadataInputDateComponent implements OnInit, AfterViewInit, OnChanges, DoCheck, OnDestroy
{
    cdr = inject(ChangeDetectorRef);
    modelGroup = inject(NgModelGroup);
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $metadataEventService = inject(MetadataEventService);
    private readonly $toastService = inject(ToastService);
    locale = inject(LOCALE_ID);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $securityService = inject(SecurityService);

    // not really readonly, but we should treat it as immutable within this component.
    @Input()
    model: Date | string;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    viewOnly?: boolean;

    @Input()
    dense?: boolean;

    @Input()
    compact?: boolean;

    @Input()
    forCellEdit?: boolean;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    hint: string;

    @Input()
    error: string;

    @Input()
    showSpinner: boolean;

    @ViewChild("pickerTextField") pickerTextField: ElementRef;
    @ViewChild("input") input: ElementRef;

    /** Accessible in the template */ readonly placeHolder = "MM/dd/yyyy";

    /** Accessible in the template */ isViewOnly: boolean;

    /** Accessible in the template */ isCalendarOpen = false;

    private _valueModel: string;

    initialized: boolean = false;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: Date | string;

    outOfRangeMsg: string = "";

    private readonly debouncer$ = new Subject<void>();
    private readonly destructor$ = new Subject<void>();

    private toastDisplayed = false;

    density: FieldDensity;

    minDate: Date;
    maxDate: Date;

    private recomputeQueued: boolean = false;

    constructor()
    {
        this.debouncer$.pipe(debounceTime(100), takeUntilDestroyed()).subscribe(() => this.fireValueChangedImmediate());
        this.$metadataService.onProxyPropertyChanged.pipe(takeUntilDestroyed(), filter((arg: ModelChangeArg) => this.$metadataService.compareModels(arg.Model, this.metadataModel)))
            .subscribe(() => this.queueComputeProperties());
    }

    ngOnInit(): void
    {
        const self = this;
        this.updateValueModel(this.model);
        this.density = this.$metadataService.getInputDensity(this.compact);
        this.computeProperties();
        if (this.propertyMetadata["$listenForNonPropertyChange"] || this.propertyMetadata.ComparedTo)
        {
            this.$metadataEventService.onRecalculateWorking.pipe(takeUntil(this.destructor$))
                .subscribe(args =>
                {
                    if (!args.Recalculating)
                        self.computeProperties();
                });
        }
    }

    ngOnDestroy(): void
    {
        this.pickerTextField = null;
        this.input = null;
        this.debouncer$.complete();
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngAfterViewInit(): void
    {
        if (this.initialized)
            return;
        this.initialized = true;
        this.cdr.detectChanges();
    }

    ngDoCheck(): void
    {
        if (!this.initialized)
        {
            return;
        }
        if (this.unboundModel !== this.model)
        {
            this.updateValueModel(this.model);
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent);
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        if (!this.initialized)
        {
            return;
        }
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    const change = _changes[key];
                    if (!change.currentValue)
                    {
                        this.valueModel = null;
                        break;
                    }
                    this.updateValueModel(change.currentValue);
                    break;
                case "metadataModel":
                case "parent":
                case "propertyMetadata":
                    this.computeProperties();
                    break;
            }
        }
    }

    get inputDisplay(): string
    {
        return this.input.nativeElement.value;
    }

    set inputDisplay(value: string)
    {
        this.input.nativeElement.value = value;
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        this._valueModel = value;
    }

    get outOfRange(): boolean
    {
        return this.outOfRangeMsg.length > 0;
    }

    get isInvalid(): boolean
    {
        return this.$metadataService.invalidInput(this.error, this.modelGroup?.control?.controls[this.propertyMetadata.Name]);
    }

    get controlId(): string
    {
        if (Utilities.IsNullOrUndefined(this.metadataModel)) return `txt_date_${this.Name}`;
        let id = this.metadataModel.$controlId;
        if (!Utilities.IsNullOrUndefined(id)) return `txt_date_${this.Name}_${id}`;
        id = this.metadataModel.$controlId = this.metadataModel.Id;
        if (Utilities.IsNullOrUndefinedOrEmpty(id) || id === EMPTY_GUID)
        {
            id = this.metadataModel.$controlId = GuidHelper.newGuid();
        }
        return `txt_date_${this.Name}_${id}`;
    }

    private updateValueModel(newValue: Date | string): void
    {
        const newValueDate = new Date(newValue);
        if (ObjectHelpers.isDate(newValueDate))
        {
            if (newValueDate.getFullYear() === 1)
            {
                newValue = null;
            }
        }
        const currentDate = this.toDate(this.valueModel);
        const newModel = ObjectHelpers.isDate(newValue) ? newValue : this.toDate(newValue);

        if (!currentDate || currentDate !== newModel)
        {
            this.unboundModel = newValue;
            if (newValue === null)
                this.model = newValue;
            this.valueModel = this.formatDate(newModel);
        }
    }

    get isRequired(): boolean
    {
        return this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) && !this.isViewOnly;
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get Label(): string
    {
        return this.forCellEdit
            ? ""
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get LabelPosition(): string
    {
        return this.forCellEdit ? 'none' : this.compact ? 'block-start' : 'inset';
    }

    get minDateString(): string
    {
        return this.minDate?.toDateString();
    }

    get maxDateString(): string
    {
        return this.maxDate?.toDateString();
    }

    queueComputeProperties(): void
    {
        if (this.recomputeQueued)
            return;
        this.recomputeQueued = true;
        requestAnimationFrame(() =>
        {
            this.recomputeQueued = false;
            this.computeProperties();
        });
    }

    computeProperties(): void
    {
        this.minDate = this.getMinDate();
        this.maxDate = this.getMaxDate();
    }

    getMinDate(): Date
    {
        const minDateProp = this.toDate(this.propertyMetadata.MinDate
            ? this.propertyMetadata.MinDate
            : !this.propertyMetadata.MinValueProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.MinValueProperty,
                    this.parent,
                    this.metadataModel
                ));
        const compareTo = this.getCompareToDate(true);
        if (!compareTo)
            return minDateProp;
        if (!minDateProp)
            return compareTo;
        return compareTo > minDateProp ? compareTo : minDateProp;
    }

    getMaxDate(): Date
    {
        const maxDateProp = this.toDate(this.propertyMetadata.MaxDate
            ? this.propertyMetadata.MaxDate
            : !this.propertyMetadata.MaxValueProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.MaxValueProperty,
                    this.parent,
                    this.metadataModel
                ));
        const compareTo = this.getCompareToDate(false);
        if (!compareTo)
            return maxDateProp;
        if (!maxDateProp)
            return compareTo;
        return compareTo < maxDateProp ? compareTo : maxDateProp;
    }

    getCompareToDate(min: boolean): Date
    {
        if (!this.propertyMetadata.ComparedTo)
            return null;
        if (min && (this.propertyMetadata.CompareType === CompareType.LessThan || this.propertyMetadata.CompareType === CompareType.LessThanOrEqualTo))
            return null;
        if (!min && (this.propertyMetadata.CompareType === CompareType.GreaterThan || this.propertyMetadata.CompareType === CompareType.GreaterThanOrEqualTo))
            return null;
        const compareModel = this.toDate(this.$metadataService.DependentModuleValue(this.propertyMetadata.ComparedTo, this.parent, this.metadataModel));
        if (!compareModel)
            return null;
        if (min && this.propertyMetadata.CompareType === CompareType.GreaterThan)
            compareModel.setDate(compareModel.getDate() + 1);
        else if (!min && this.propertyMetadata.CompareType === CompareType.LessThan)
            compareModel.setDate(compareModel.getDate() - 1);
        return compareModel;
    }

    get yearRange(): string
    {
        const currentYear = new Date().getFullYear();
        let min = 100;
        let max = 20;

        if (this.minDate)
        {
            min = currentYear - this.minDate.getFullYear();
        }

        if (this.maxDate)
        {
            max = this.maxDate.getFullYear() - currentYear;
        }

        return `-${min}:+${max}`;
    }

    showInvalidEntryMessage(message: string): void
    {
        if (!this.toastDisplayed)
        {
            const self = this;
            this.toastDisplayed = true;

            this.$toastService.showToastPromise(message, 2000, "error")
                .then(() =>
                {
                    self.toastDisplayed = false;
                });
        }
    }

    testValidDate(): boolean
    {
        if (!this.valueModel)
            return true; //null is valid
        if (this.valueModel.indexOf("/") === -1)
        {
            if (this.valueModel.length === 8)
            {
                this.valueModel = `${this.valueModel.substring(0, 2)}/${this.valueModel.substring(
                    2,
                    4
                )}/${this.valueModel.substring(4, 8)}`;
                return true;
            }
            else return false;
        }
        return VALID_DATE_PATTERN.test(this.valueModel);
    }

    onInputChange(event): void
    {
        const hasValue = !Utilities.IsNullOrUndefinedOrEmpty(this.inputDisplay);
        this.pickerTextField.nativeElement.floatLabel = hasValue;
        if (isNaN(parseInt(event.data)) && event.data !== "/" && event.data !== null)
        {
            event.stopImmediatePropagation();
        }
        this.debouncer$.next();
    }

    fireValueChangedImmediate(): void
    {
        const today = new Date();
        let fireChange = false;
        const value = Utilities.IsNullOrUndefined(this.inputDisplay) ? "" : this.inputDisplay;
        switch (value.toLowerCase())
        {
            case BEGINNING_OF_WEEK: {
                const day = today.getDay();
                this.valueModel = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate() - day
                )
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_WEEK: {
                const day = 6 - today.getDay();
                this.valueModel = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate() + day
                )
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_MONTH: {
                this.valueModel = new Date(today.getFullYear(), today.getMonth(), 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_MONTH: {
                this.valueModel = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_LAST_MONTH: {
                this.valueModel = new Date(today.getFullYear(), today.getMonth() - 1, 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_LAST_MONTH: {
                this.valueModel = new Date(today.getFullYear(), today.getMonth(), 0)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_QUARTER: {
                const quarter = Math.floor(today.getMonth() / 3 + 1) - 1;
                this.valueModel = new Date(today.getFullYear(), quarter * 3, 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_QUARTER: {
                const quarter = Math.floor(today.getMonth() / 3 + 1) - 1;
                this.valueModel = new Date(today.getFullYear(), quarter * 3 + 3, 0)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_YEAR: {
                this.valueModel = new Date(today.getFullYear(), 0, 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_YEAR: {
                this.valueModel = new Date(today.getFullYear(), 11, 31)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_LAST_QUARTER: {
                const quarter = Math.floor(today.getMonth() / 3 + 1) - 2;
                this.valueModel = new Date(today.getFullYear(), quarter * 3, 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_LAST_QUARTER: {
                const quarter = Math.floor(today.getMonth() / 3 + 1) - 2;
                this.valueModel = new Date(today.getFullYear(), quarter * 3 + 3, 0)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_LAST_YEAR: {
                this.valueModel = new Date(today.getFullYear() - 1, 0, 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_LAST_YEAR: {
                this.valueModel = new Date(today.getFullYear() - 1, 11, 31)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            default:
                break;
        }

        if (fireChange === true)
        {
            this.inputDisplay = this.valueModel;
            this.fireValueChanged();
        }
    }

    fireValueChanged(): void
    {
        if (MONTH_DAY_PATTERN.test(this.inputDisplay))
        {
            const today = new Date();
            this.valueModel = new Date(`${this.inputDisplay}${today.getFullYear()}`).toLocaleDateString("en-US");
            this.inputDisplay = this.valueModel;
        }

        const modelAsDate = ObjectHelpers.isDate(this.model) ? this.model : this.toDate(this.model);
        let currentDate = this.toDate(this.inputDisplay);

        if (Utilities.IsNullOrUndefined(currentDate) || currentDate.getFullYear() > 2200)
        {
            currentDate = null;
        }

        if (!ObjectHelpers.isDate(currentDate))
        {
            this.valueModel = null;
        }
        else
        {
            this.computeProperties();
            if (!Utilities.IsNullOrUndefined(this.maxDate) && currentDate > this.maxDate)
            {
                this.outOfRangeMsg = `Must be on or before ${this.formatDate(this.maxDate)}`;
            }
            else if (!Utilities.IsNullOrUndefined(this.minDate) && currentDate < this.minDate)
            {
                this.outOfRangeMsg = `Must be on or after ${this.formatDate(this.minDate)}`;
            }
            else
            {
                this.outOfRangeMsg = "";
            }
            this.error = this.outOfRangeMsg;
            if (this.forCellEdit && this.outOfRange)
            {
                this.showInvalidEntryMessage(this.outOfRangeMsg);
            }
        }

        if (MetadataInputDateComponent.areEqual(modelAsDate, currentDate))
        {
            if (currentDate && !this.testValidDate())
            {
                // if the valueModel isn't correctly formatted, but is a parse-able date, then lets apply formatting to the text value.
                this.updateValueModel(currentDate);
            }
            return;
        }
        if (
            (this.valueModel && !currentDate) // if we have a value that can't be turned into date value, don't push value up.
        )
        {
            return;
        }
        this.onValueChanged.emit({ value: currentDate });
    }

    private static areEqual(a: Date, b: Date): boolean
    {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.getTime() === b.getTime();
    }

    get showDate(): string
    {
        return this.valueModel;
    }

    private isValidDate(date: Date): boolean
    {
        return !isNaN(date.getTime());
    }

    private toDate(value: string): Date
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(value))
        {
            return null;
        }
        const date = new Date(this.formatDate(value));
        if (!this.isValidDate(date)) return null;
        return date;
    }

    private formatDate(value: Date | string): string
    {
        if (Utilities.IsNullOrUndefined(value))
        {
            return null;
        }
        if (typeof (value) === "string" && value.length >= 6 && value.indexOf("/") < 0 && value.indexOf("-") < 0)
        {
            value = `${value.substring(0, 2)}/${value.substring(2, 4)}/${value.substring(4)}`;
        }
        if (this.isValidDate(new Date(value)))
        {
            return formatDate(value, "MM/dd/yyyy", this.locale);
        }
        else
        {
            return value.toString();
        }
    }

    onKeyDown(event: KeyboardEvent): void
    {
        if (event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
    }
}
